import { useEffect, useRef, useState } from 'react';
import type { SearchResult } from './types';

export interface UseSearchBoxOptions {
  value?: string;
  selectedResult?: SearchResult | null;
  onSelect?: (result: SearchResult | null) => void;
  onChange?: (search: string) => void;
  onClearSelection?: () => void;
  searchResults?: SearchResult[];
  onConfirm?: () => void;
}

export interface UseSearchBoxReturn {
  displayValue: string;
  selectedResult: SearchResult | null;
  highlightedIndex: number;
  openSearchResults: boolean;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelect: (item: SearchResult | null) => void;
  handleClearSelected: () => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  handleFocus: () => void;
  handleBlur: () => void;
  setHighlightedIndex: (index: number) => void;
  setOpenSearchResults: (open: boolean) => void;
  textFieldRef: React.RefObject<HTMLDivElement>;
}

export const useSearchBox = ({
  value,
  selectedResult: controlledSelected,
  onSelect,
  onChange,
  onClearSelection,
  searchResults,
  onConfirm,
}: UseSearchBoxOptions): UseSearchBoxReturn => {
  const [displayValue, setDisplayValue] = useState(value || '');
  const [uncontrolledSelected, setUncontrolledSelected] =
    useState<SearchResult | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [openSearchResults, setOpenSearchResults] = useState(false);

  const textFieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value !== undefined) {
      setDisplayValue(value);
    }
  }, [value]);

  const selectedResult =
    controlledSelected !== undefined
      ? controlledSelected
      : uncontrolledSelected;

  // When a result is selected, refocus the input so user can press Enter
  useEffect(() => {
    if (selectedResult && !displayValue) {
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 50);
    }
  }, [selectedResult, displayValue]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setDisplayValue(value);
    onChange?.(value);
  };

  const handleSelect = (item: SearchResult | null) => {
    onSelect?.(item);
    if (controlledSelected === undefined) {
      setUncontrolledSelected(item);
    }

    setDisplayValue('');
    onChange?.('');
  };

  const handleClearSelected = () => {
    if (onClearSelection) {
      onClearSelection();
    } else {
      handleSelect(null);
    }
    textFieldRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!displayValue && selectedResult && e.key === 'Backspace') {
      e.preventDefault();
      handleClearSelected();
      return;
    }

    if (searchResults?.length) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev + 1) % searchResults.length);
          return;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev <= 0 ? searchResults.length - 1 : prev - 1,
          );
          return;
        case 'Enter':
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < searchResults.length
          ) {
            e.preventDefault();
            const selected = searchResults[highlightedIndex];
            handleSelect(selected);
            return;
          }
          break;
        case 'Escape':
          e.preventDefault();
          setHighlightedIndex(-1);
          return;
        default:
          break;
      }
    }

    if ((selectedResult || displayValue) && e.key === 'Enter' && onConfirm) {
      e.preventDefault();
      onConfirm();
      return;
    }
  };

  const handleFocus = () => {
    setOpenSearchResults(true);
  };

  const handleBlur = () => {
    setOpenSearchResults(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on both displayValue and searchResults changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [displayValue, searchResults]);

  return {
    displayValue,
    selectedResult,
    highlightedIndex,
    openSearchResults,
    handleSearchChange,
    handleSelect,
    handleClearSelected,
    handleKeyDown,
    handleFocus,
    handleBlur,
    setHighlightedIndex,
    setOpenSearchResults,
    textFieldRef,
  };
};
